import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PageContentContext
 *
 * Provides a `useContent(pageKey, sectionKey, fallback)` hook that
 * returns admin-overridden content when available and the hardcoded
 * fallback otherwise. Cache is keyed per page_key and lazy-loaded.
 */
const PageContentContext = createContext({
  get: (_page, _section, fb) => fb,
  ensurePage: () => Promise.resolve(),
  bump: () => {},
});

export const PageContentProvider = ({ children }) => {
  // pages: { home: { "hero.title": "..." }, "our-story": {...} }
  const [pages, setPages] = useState({});
  const [loaded, setLoaded] = useState({});
  const [version, setVersion] = useState(0);

  const ensurePage = useCallback(async (pageKey) => {
    if (!pageKey || loaded[pageKey]) return;
    try {
      const { data } = await axios.get(`${API}/content/${pageKey}`);
      setPages((prev) => ({ ...prev, [pageKey]: data || {} }));
    } catch {
      setPages((prev) => ({ ...prev, [pageKey]: {} }));
    } finally {
      setLoaded((l) => ({ ...l, [pageKey]: true }));
    }
  }, [loaded]);

  /** Force refresh of a single page (called after admin saves) */
  const refreshPage = useCallback(async (pageKey) => {
    try {
      const { data } = await axios.get(`${API}/content/${pageKey}`);
      setPages((prev) => ({ ...prev, [pageKey]: data || {} }));
      setVersion((v) => v + 1);
    } catch { /* ignore */ }
  }, []);

  const get = useCallback((page, section, fallback = "") => {
    const bucket = pages[page];
    if (!bucket) return fallback;
    const v = bucket[section];
    return (v === undefined || v === null || v === "") ? fallback : v;
  }, [pages]);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  return (
    <PageContentContext.Provider value={{ get, ensurePage, refreshPage, bump, version }}>
      {children}
    </PageContentContext.Provider>
  );
};

/**
 * useContent — returns admin-overridden text/URL for a section, or the
 * fallback if not yet edited. Automatically triggers a lazy fetch of
 * the page's content bucket on first read.
 *
 * Usage:
 *   const title = useContent("home", "hero.title", "A quiet return...");
 */
export const useContent = (pageKey, sectionKey, fallback = "") => {
  const ctx = useContext(PageContentContext);
  useEffect(() => {
    ctx.ensurePage(pageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);
  return ctx.get(pageKey, sectionKey, fallback);
};

/**
 * <EditableText> — thin wrapper. Renders admin-overridden or fallback
 * text. `as` picks the tag (default span). Prop `html` renders raw HTML.
 * A tiny orange dot appears on the site only for signed-in admin
 * (localStorage flag `hk_admin_pw` present) — pass showAdminHint={false}
 * to disable.
 */
export const EditableText = ({
  page,
  section,
  fallback,
  as: Tag = "span",
  html = false,
  className = "",
  ...rest
}) => {
  const value = useContent(page, section, fallback);
  if (html) {
    return <Tag className={className} dangerouslySetInnerHTML={{ __html: value }} {...rest} />;
  }
  return <Tag className={className} {...rest}>{value}</Tag>;
};

/** Access the raw context if you need refreshPage() (used by admin). */
export const usePageContentContext = () => useContext(PageContentContext);
